import {
  IconStar,
  IconAlertCircle,
  IconInfoCircle,
  IconMessageReport,
  IconAlertTriangle,
  IconCheck,
  IconChevronUp,
  IconChevronLeft,
  IconCirclePlus,
  IconBook,
  IconChalkboardTeacher,
  IconSmartHome,
  IconUsers,
  IconClipboardList,
  IconDeviceIpadHorizontalSearch,
  IconCurrencyDollar,
  IconCreditCard,
  IconCreditCardPay,
  IconBrandPaypal,
  IconBrandGmail,
  IconCalendar,
  IconCalendarEvent,
  IconFileText,
  IconFileAnalytics,
  IconChartLine,
  IconFilePercent,
  IconReportMedical,
  IconReportMoney,
  IconShieldCheck,
  IconUser,
  IconUserPlus,
  IconMapPin,
  IconMapSearch,
  IconEye,
  IconEyeClosed,
  IconMail,
  IconMailCheck,
  IconMailX,
  IconUserCheck,
  IconPhone,
  IconPhoneCheck,
  IconPhoneX,
  IconShield,
  IconCircleDashedCheck,
  IconProgressCheck,
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconQrcode,
  IconDeviceMobileMessage,
  IconDeviceDesktopCheck,
  IconDeviceTabletCheck,
  IconLogout2,
  IconRefresh,
  IconRefreshAlert,
  IconDownload,
  IconTrash,
  IconPhoto,
  IconUsersGroup,
  IconCoin,
  IconCoinOff,
  IconBabyCarriage,
  IconOld,
  IconBold,
  IconItalic,
  IconBlockquote,
  IconLink,
  IconThumbUp,
  IconMoodSmile,
  IconBan,
  IconBell,
  IconBellCheck,
  IconBellRinging,
  IconAdjustmentsHorizontal,
  IconSearch,
  IconCopy,
  IconSchool,
  IconClock,
  IconClockHour3,
  IconHourglassLow,
  IconSunrise,
  IconSun,
  IconSunglasses,
  IconSunset,
  IconMoon,
  IconNotes,
  IconBackpack,
  IconTrafficCone,
  IconCloud,
  IconCloudRain,
  IconCloudSnow,
  IconCloudStorm,
  IconWind,
  IconSnowflake,
  IconBarrierBlock,
  IconBuildingChurch,
  IconBuildings,
  IconMailbox,
  IconPhoneCall,
  IconBible,
  IconHeartHandshake,
  IconCross,
  IconConfetti,
  IconBalloon,
  IconAward,
  IconLeaf2,
  IconChristmasTree,
  IconFishChristianity,
  IconPray,
  IconToolsKitchen2,
  IconGridDots,
  IconX,
} from '@tabler/icons-react';

import {
  getIconConfig,
  iconSizes,
  iconBackgroundOpacity,
  iconColors,
  type IconType,
  type IconStyle,
  type IconBackgroundShape,
  type IconColorVariant,
  type IconSize,
} from '@design-tokens/icons';

import { hexToRgba, buildClassName } from '@utils';

import './Icon.css';

export interface IconProps {
  /**
   * Icon type
   */
  type: IconType;
  /**
   * Icon style variant
   * @default 'outlined'
   */
  style?: IconStyle;
  /**
   * Background shape
   * @default 'circle'
   */
  backgroundShape?: IconBackgroundShape;
  /**
   * Color variant
   * @default 'blue'
   */
  colorVariant?: IconColorVariant;
  /**
   * Icon size
   * @default 'md'
   */
  size?: IconSize;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Accessibility label
   */
  'aria-label'?: string;
}

// Icon component mapping.
// New Tabler icons: also update `IconType` in design-tokens/icons.ts and, for announcement banners,
// `ANNOUNCEMENT_ICON_REGISTRY` in lambda/shared/announcementIcons.ts — see tabler-icons-announcements.mdc.
const iconComponents = {
  star: IconStar,
  'exclamation-circle': IconAlertCircle,
  'info-circle': IconInfoCircle,
  'message-report': IconMessageReport,
  'exclamation-triangle': IconAlertTriangle,
  checkmark: IconCheck,
  'chevron-up': IconChevronUp,
  'chevron-left': IconChevronLeft,
  'circle-plus': IconCirclePlus,
  book: IconBook,
  'chalkboard-teacher': IconChalkboardTeacher,
  'smart-home': IconSmartHome,
  users: IconUsers,
  'clipboard-list': IconClipboardList,
  'device-ipad-horizontal-search': IconDeviceIpadHorizontalSearch,
  'currency-dollar': IconCurrencyDollar,
  'credit-card': IconCreditCard,
  'credit-card-pay': IconCreditCardPay,
  'brand-paypal': IconBrandPaypal,
  'brand-gmail': IconBrandGmail,
  calendar: IconCalendar,
  'calendar-event': IconCalendarEvent,
  'file-text': IconFileText,
  'file-analytics': IconFileAnalytics,
  'chart-line': IconChartLine,
  'file-percent': IconFilePercent,
  'report-medical': IconReportMedical,
  'report-money': IconReportMoney,
  'shield-check': IconShieldCheck,
  user: IconUser,
  'user-plus': IconUserPlus,
  'map-pin': IconMapPin,
  'map-search': IconMapSearch,
  eye: IconEye,
  'eye-off': IconEyeClosed,
  mail: IconMail,
  'mail-check': IconMailCheck,
  'mail-x': IconMailX,
  'user-check': IconUserCheck,
  phone: IconPhone,
  'phone-check': IconPhoneCheck,
  'phone-x': IconPhoneX,
  shield: IconShield,
  'circle-dashed-check': IconCircleDashedCheck,
  'progress-check': IconProgressCheck,
  'circle-check': IconCircleCheck,
  'circle-minus': IconCircleMinus,
  'circle-x': IconCircleX,
  qrcode: IconQrcode,
  'device-mobile-message': IconDeviceMobileMessage,
  'device-desktop-check': IconDeviceDesktopCheck,
  'device-tablet-check': IconDeviceTabletCheck,
  'logout-2': IconLogout2,
  refresh: IconRefresh,
  'refresh-alert': IconRefreshAlert,
  download: IconDownload,
  trash: IconTrash,
  photo: IconPhoto,
  'users-group': IconUsersGroup,
  coin: IconCoin,
  'coin-off': IconCoinOff,
  'baby-carriage': IconBabyCarriage,
  old: IconOld,
  bold: IconBold,
  italic: IconItalic,
  blockquote: IconBlockquote,
  link: IconLink,
  'thumb-up': IconThumbUp,
  'mood-smile': IconMoodSmile,
  ban: IconBan,
  bell: IconBell,
  'bell-check': IconBellCheck,
  'bell-ringing': IconBellRinging,
  'adjustments-horizontal': IconAdjustmentsHorizontal,
  search: IconSearch,
  copy: IconCopy,
  school: IconSchool,
  clock: IconClock,
  'clock-hour-3': IconClockHour3,
  'hourglass-low': IconHourglassLow,
  sunrise: IconSunrise,
  sun: IconSun,
  sunglasses: IconSunglasses,
  sunset: IconSunset,
  moon: IconMoon,
  notes: IconNotes,
  backpack: IconBackpack,
  'traffic-cone': IconTrafficCone,
  cloud: IconCloud,
  'cloud-rain': IconCloudRain,
  'cloud-snow': IconCloudSnow,
  'cloud-storm': IconCloudStorm,
  wind: IconWind,
  snowflake: IconSnowflake,
  'barrier-block': IconBarrierBlock,
  'building-church': IconBuildingChurch,
  buildings: IconBuildings,
  mailbox: IconMailbox,
  'phone-call': IconPhoneCall,
  bible: IconBible,
  'heart-handshake': IconHeartHandshake,
  cross: IconCross,
  confetti: IconConfetti,
  balloon: IconBalloon,
  award: IconAward,
  'leaf-maple': IconLeaf2,
  'christmas-tree': IconChristmasTree,
  'fish-christianity': IconFishChristianity,
  pray: IconPray,
  'tools-kitchen-2': IconToolsKitchen2,
  'grid-dots': IconGridDots,
  x: IconX,
};

export function Icon({
  type,
  style = 'outlined',
  backgroundShape = 'circle',
  colorVariant = 'blue',
  size = 'md',
  className = '',
  'aria-label': ariaLabel,
}: IconProps) {
  const config = getIconConfig(type, style, backgroundShape, colorVariant, size);
  const IconComponent = iconComponents[type];
  const iconSize = iconSizes[size];
  const isOutlined = style === 'outlined';
  const backgroundOpacity = iconBackgroundOpacity[style];

  const backgroundColor = isOutlined
    ? hexToRgba(config.backgroundColor ?? iconColors.light.blue, backgroundOpacity)
    : (config.backgroundColor ?? iconColors.dark.blue);

  const iconClasses = buildClassName(
    'bhg-icon',
    `bhg-icon--${type}`,
    `bhg-icon--${style}`,
    `bhg-icon--${backgroundShape}`,
    `bhg-icon--${colorVariant}`,
    `bhg-icon--${size}`,
    className
  );

  const innerSize = iconSize * 0.6; // Icon graphic is 60% of container

  return (
    <div
      className={iconClasses}
      style={{
        width: iconSize,
        height: iconSize,
        borderRadius: config.borderRadius,
        backgroundColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-label={ariaLabel}
      role="img"
    >
      <span
        className="bhg-icon__inner"
        style={{
          width: innerSize,
          height: innerSize,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconComponent
          size={innerSize}
          strokeWidth={isOutlined ? 2 : 2}
          fill="none"
          color={config.iconColor}
          style={{
            width: innerSize,
            height: innerSize,
          }}
        />
      </span>
    </div>
  );
}
